/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useLayoutEffect, useState } from 'react'
import Cookies from 'js-cookie'
import Button from '@/src/components/button'
import {
  Box,
  CircularProgress,
  Divider,
  Modal,
  Skeleton,
  TextField,
  Typography
} from '@mui/material'
import LoginForm, { type Props as LoginFormProps } from '@/src/components/login-form/LoginForm'
import { useRouter } from 'next/router'
import AuthBanner from '@/src/components/auth/AuthBanner'
import CommonApi from '@/pages/api'
import Link from 'next/link'
import { handleSessionVisits } from '@/src/types/helper'
import { useStore } from '@/src/types/store'
import BlockUserPage from '@/src/components/signin-blocked'
import { useTier } from '@/src/hooks/useTier'
import TwoFactorForm from '@/src/components/two-factor/TwoFactorForm'
import TotpSetupWizard from '@/src/components/two-factor/TotpSetupWizard'
import {
  isSessionExpired,
  type LoginResponse,
  type LoginStep
} from '@/src/components/two-factor/loginFlow'
import {
  clearLoginChallenge,
  loadLoginChallenge,
  saveLoginChallenge,
  type LoginChallenge
} from '@/src/components/two-factor/twoFactorSession'
import { ProfileEndpoint, TwoFactorEndpoints } from '@/pages/api/endpoints'
import dayjs from 'dayjs'
import { validatePath } from '@/src/common'
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4
}

// Staff user types that belong in the admin portal. Mirrors ADMIN_USER_TYPES
// in relait-widgets (src/utils/widgetAuth.js): "user" is a pre-registration
// placeholder and "investor" has no admin portal, so neither is listed.
const ADMIN_USER_TYPES = ['admin', 'business', 'sub_admin']

// Layout effects run before the browser paints, but React 18 warns when one
// is rendered on the server, and this page is server-rendered.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

// A widget popup opened while this browser already holds a session. Read from
// the URL rather than router.query, which is still empty on the first client
// render of a statically optimised page and would delay the check.
const isWidgetPopupWithSession = () =>
  !!window.opener &&
  new URLSearchParams(window.location.search).get('redirect_url') === '__widget_login__' &&
  !!Cookies.get('access_token')

const Login = () => {
  const [verificationModal, setVerificationModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [userName, setUserName] = useState('')
  const [blockedUser, setBlockedUser] = useState(null)
  const [step, setStep] = useState<LoginStep>('credentials')
  const [challenge, setChallenge] = useState<LoginChallenge | null>(null)
  const [setupToken, setSetupToken] = useState('')
  // True while the widget popup checks for an admin session this browser
  // already holds, so the sign-in form doesn't flash before it closes.
  const [resumingSession, setResumingSession] = useState(false)

  const router = useRouter()
  const customRedirectUrl: any = router?.query?.redirect_url ?? null
  const fullUrl = router.asPath
  let redirectUrl = '/'
  if (fullUrl.includes('redirect_url=')) {
    const redirectPart = fullUrl.split('redirect_url=')[1]
    redirectUrl = decodeURIComponent(redirectPart)
  }
  const safeRedirectUrl = validatePath(customRedirectUrl?.split('?')[0]) ? redirectUrl : '/'
  const { redirectionUrl, themeLogo, themeLoading, setProfile, openToast } = useStore()
  const [isPathPortal, setIsPathPortal] = useState(false)
  // Investor accounts only exist on 360. On the other tiers a new account
  // could be created but never signed in with, so the Register link goes.
  const { canInvestorLogin } = useTier()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsPathPortal(window.location.hostname.startsWith('portal'))
    }
  }, [])

  // Everything that used to run inline once `login` answered with a token. The
  // 2FA steps finish on the same code path once their own call returns it.
  const completeLogin = (response: LoginResponse) => {
    // Never write the cookie without a token — it would land as the string
    // "undefined" and every later request would go out with a bad Bearer.
    if (!response?.access_token) {
      openToast(response?.msg || 'Something went wrong. Please sign in again.', 'error')
      backToCredentials()
      return
    }
    openToast('Login successfully!', 'success')
    Cookies.set('access_token', response.access_token)
    // Opened by a widget popup: hand over to the client site and close
    if (customRedirectUrl === '__widget_login__' && window.opener) {
      handOffToWidget(response)
      return
    }

    if (response?.data?.user_type === 'investor') {
      handleSessionVisits(false, true)
    }

    if (redirectionUrl) {
      router.push(redirectionUrl)
    } else {
      if (response?.data?.user_type === 'investor') {
        router.push('/')
      } else {
        if (customRedirectUrl && customRedirectUrl !== '__widget_login__') {
          router.push(`${safeRedirectUrl}`)
        } else {
          router.push('/admin')
        }
      }
    }
  }

  // An admin who is already signed in here clicks "Join" on a client website.
  // Asking for credentials again is what the widget ticket was about, but the
  // client site must still never hold an admin token. So the token stays put:
  // once /profile confirms a staff account, sendOpenerToAdminPortal sends the
  // client site's tab straight to the admin portal and the popup closes.
  //
  // Investors are deliberately not resumed this way. Doing it would mean
  // posting their token to whatever page opened the popup, with no click from
  // them, and any site can open this URL. They keep signing in as before.
  //
  // An expired cookie never gets here (the middleware drops it), and a revoked
  // one makes /profile answer 403, which CommonApi handles by clearing the
  // cookie and reloading this page: the form then shows as usual.
  //
  // A layout effect, so the spinner replaces the form before the first paint
  // after hydration rather than one frame later. It can't be the initial state
  // instead: the server has no window.opener, and seeding state from it would
  // make the client's first render disagree with the server HTML.
  useIsomorphicLayoutEffect(() => {
    if (!isWidgetPopupWithSession()) return

    let cancelled = false
    setResumingSession(true)
    const resume = async () => {
      try {
        const response = await CommonApi({ path: ProfileEndpoint.get, method: 'get' })
        if (cancelled) return
        if (ADMIN_USER_TYPES.includes(response?.data?.user_type)) {
          sendOpenerToAdminPortal()
          return
        }
      } catch {
        // Any failure falls through to the normal sign-in form.
      }
      if (!cancelled) setResumingSession(false)
    }
    resume()
    return () => {
      cancelled = true
    }
  }, [])

  // Staff go to the admin portal by navigating the client site's tab. That is
  // allowed cross-origin and hands the opener nothing but a URL, so an admin
  // token never reaches the client site, whichever page opened this popup.
  const sendOpenerToAdminPortal = () => {
    window.opener.location.href = `${window.location.origin}/admin`
    window.close()
  }

  // Finishes a sign-in made from a widget popup. Only an investor's token is
  // posted back, because the widget keeps a session on the client site for
  // investors only. postMessage goes to '*': widgets run on many client
  // domains this app doesn't know, so the token must never be one worth
  // stealing, and staff tokens are. If the login response doesn't say who
  // this is, /profile decides; if that fails too, nothing is shared.
  const handOffToWidget = async (response: LoginResponse) => {
    let userType = response?.data?.user_type
    if (!userType) {
      try {
        const profile = await CommonApi({ path: ProfileEndpoint.get, method: 'get' })
        userType = profile?.data?.user_type
      } catch {
        userType = undefined
      }
    }

    if (userType === 'investor') {
      window.opener.postMessage(
        { type: 'RELAIT_WIDGET_LOGIN', access_token: response.access_token },
        '*'
      )
      window.close()
      return
    }

    if (userType && ADMIN_USER_TYPES.includes(userType)) {
      try {
        sendOpenerToAdminPortal()
        return
      } catch {
        // the client site's tab is gone: fall through and land here instead
      }
      router.push('/admin')
      return
    }

    // A pre-registration "user", or a type we couldn't confirm: no token is
    // shared and the popup just closes; the client site stays signed out.
    window.close()
  }

  const backToCredentials = () => {
    clearLoginChallenge()
    setChallenge(null)
    setSetupToken('')
    setStep('credentials')
  }

  // A refresh mid-step resumes the code box while the challenge is still valid
  useEffect(() => {
    const saved = loadLoginChallenge()
    if (saved) {
      setChallenge(saved)
      setUserName(saved.email)
      setStep('code')
    }
  }, [])

  const handleFormValues: LoginFormProps['onSubmit'] = async values => {
    setProfile((prev: any) => ({ ...prev, user_type: '' }))
    setUserName(values?.email)
    const authenticationData = {
      email: values?.email,
      password: values?.password
    }
    try {
      setLoading(true)

      const response = await CommonApi({
        path: 'login',
        method: 'post',
        data: authenticationData
      })

      // 2FA answers come back with status:true but no access_token — check them
      // before the cookie write, otherwise the cookie becomes "undefined".
      if (response?.two_factor_required) {
        const expiresIn = Number(response?.expires_in) || 180
        const nextChallenge: LoginChallenge = {
          challenge_id: response?.challenge_id,
          email: values?.email,
          email_hint: response?.email_hint,
          expires_at: Date.now() + expiresIn * 1000
        }
        saveLoginChallenge(nextChallenge)
        setChallenge(nextChallenge)
        setStep('code')
        return
      }

      if (response?.two_factor_setup_required) {
        // setup_token lives in state only — never a cookie, never localStorage
        setSetupToken(response?.setup_token)
        setStep('setup')
        return
      }

      if (response.status) {
        completeLogin(response)
      }
    } catch (error: any) {
      if (error?.response?.data?.msg == 'User is not confirmed.') {
        setVerificationModal(true)
      }
      if (error?.response?.data?.blocked) {
        setBlockedUser(error?.response?.data)
      }
      openToast(error?.response?.data?.msg || 'Invalid business user!', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (code: string) => {
    if (!challenge) return
    try {
      setLoading(true)
      const response = await CommonApi({
        path: TwoFactorEndpoints.loginVerify,
        method: 'post',
        data: {
          email: challenge.email,
          challenge_id: challenge.challenge_id,
          code
        }
      })

      if (response?.status && response?.access_token) {
        clearLoginChallenge()
        setChallenge(null)
        completeLogin(response)
        return
      }
      openToast(response?.msg || 'Invalid verification code. Please try again.', 'error')
    } catch (error: any) {
      const msg = error?.response?.data?.msg
      openToast(msg || 'Invalid verification code. Please try again.', 'error')
      // an expired challenge cannot be retried — send the user back to the password
      if (isSessionExpired(error)) {
        backToCredentials()
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVerifiyUser = async () => {
    try {
      setLoading(true)
      const response = await CommonApi({
        path: 'confirm-signup',
        method: 'post',
        data: {
          email: userName,
          confirmation_code: verificationCode
        }
      })
      if (response.status) {
        setIsVerified(true)
        openToast(response?.msg ?? 'Verified successfully!', 'success')
        router.push('/login')
        setVerificationModal(false)
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Invalid code!', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerificationCode = async () => {
    try {
      const response = await CommonApi({
        path: 'resend-confirmation-code',
        method: 'post',
        data: {
          email: userName
        }
      })
      if (response.status) {
        openToast('Confirmation code sent successfully!', 'success')
      }
    } catch (error: any) {
      openToast(error?.response?.data?.msg || 'Something went wrong', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="flex w-full h-[100dvh] justify-between">
        <div className="w-full md:w-[50vw] flex justify-center pt-8 pb-16 md:py-0 md:items-center relative">
          <div className="flex flex-col my-auto md:my-0 relative ">
            <div className="w-full flex justify-center">
              {themeLoading ? (
                <Skeleton className="h-[4.75rem] w-[10rem]" />
              ) : !window.location.hostname.startsWith('portal') ? (
                <Link href="/">
                  <img src={themeLogo.logo} alt="Logo" className="max-w-[20.75rem] h-[2.75rem] object-contain" />
                </Link>
              ) : (
                <img src={themeLogo.logo} alt="Logo" className="max-w-[20.75rem] h-[2.75rem] object-contain" />
              )}
            </div>
            {resumingSession ? (
              <div className="flex justify-center pt-8 md:pt-16">
                <CircularProgress />
              </div>
            ) : blockedUser ? (
              <BlockUserPage
                handleBack={setBlockedUser}
                blockedUserData={blockedUser}
                loading={loading}
              />
            ) : step === 'code' && challenge ? (
              <div className="pt-8 md:pt-16">
                <TwoFactorForm
                  key={challenge.challenge_id}
                  loading={loading}
                  emailHint={challenge.email_hint}
                  expiresIn={Math.max(0, Math.round((challenge.expires_at - Date.now()) / 1000))}
                  onVerify={handleVerifyCode}
                  onBack={backToCredentials}
                />
              </div>
            ) : step === 'setup' ? (
              <div className="pt-8 md:pt-16">
                <TotpSetupWizard
                  mode="login"
                  setupToken={setupToken}
                  compact={customRedirectUrl === '__widget_login__'}
                  onComplete={completeLogin}
                  onCancel={backToCredentials}
                />
              </div>
            ) : (
              <div>
                <Typography variant="h1" className="pb-8 pt-8 md:pt-16 text-center">
                  Sign In
                </Typography>
                <LoginForm loading={loading} onSubmit={handleFormValues} isDisable={false} />
                <Box className="flex gap-2 w-full pt-6 justify-center sm:justify-start relative items-center">
                  {!isPathPortal && canInvestorLogin && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {"Don't have an account?"}
                      </Typography>
                      <Link
                        href={{
                          pathname: '/create-account',
                          query: { redirect_url: '__widget_login__' }
                        }}
                      >
                        <Typography variant="buttonLink" color="primary.main">
                          Register
                        </Typography>
                      </Link>
                    </>
                  )}
                </Box>
              </div>
            )}
          </div>
          <div className="text-secondary-main w-full pl-4 flex justify-start absolute gap-4 bottom-4 left-0 md:left-8">
            <Typography variant="caption">© {dayjs().format('YYYY')} Relait PTY LTD</Typography>
            {'  '}
            <div className="flex items-center gap-2">
              <Typography variant="buttonLarge_sm" color="text.secondary">
                <a
                  href="https://www.relait.com.au/privacy-policy"
                  target="_blank"
                  className="hover:underline"
                >
                  Privacy
                </a>
              </Typography>
              <Divider
                orientation="vertical"
                className="h-3 w-px items-center flex justify-center"
              />
              <Typography variant="buttonLarge_sm" color="text.secondary">
                <Link
                  href="https://www.relait.com.au/eula"
                  target="_blank"
                  className="hover:underline"
                >
                  Terms
                </Link>
              </Typography>
            </div>
          </div>
        </div>
        <div className="hidden md:flex justify-end w-[50vw] h-full">
          <AuthBanner />
        </div>
      </div>

      {/* Verification Modal */}

      <Modal open={verificationModal}>
        <Box sx={style}>
          {loading && (
            <div className="w-full flex flex-col justify-center items-center">
              <CircularProgress />
              <Typography>Processing</Typography>
            </div>
          )}
          {!loading && (
            <div>
              {isVerified ? (
                <Typography> Registered Successfully!</Typography>
              ) : (
                <>
                  <Typography>
                    {' '}
                    You are un verified user, check you email and enter your verification code.
                  </Typography>

                  <TextField
                    label="Verification Code"
                    fullWidth
                    margin="normal"
                    onChange={e => setVerificationCode(e.target.value)}
                  />
                  <div className="flex gap-6 justify-center mt-4">
                    <Button
                      variant="outlined"
                      onClick={handleResendVerificationCode}
                      text="Resend"
                    />

                    <Button
                      // className="bg-blue-500"
                      variant="contained"
                      // disableElevation
                      onClick={handleVerifiyUser}
                      text="Confirm"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </Box>
      </Modal>
    </div>
  )
}

export default Login
